import React, { useState, useContext, useEffect } from 'react';
import { auth, db } from '../utils/firebaseConfig';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { doc, getDoc, updateDoc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight, FiShield, FiUser, FiStar, FiTrendingUp, FiAward } from 'react-icons/fi';
import { AuthContext } from '../contexts/AuthContext';
import { loginRateLimiter } from '../utils/rateLimiter';
import { twoFactorAuth } from '../utils/twoFactorAuth';
import { logUserActivity } from '../utils/auditLogger';
import logo from '../assets/logo.png';
import { presenceTracker } from '../utils/presenceTracker';
// import { useTheme } from '../contexts/ThemeContext'; // Removed - no longer needed

// Removed animations for simplified experience

// Removed page loader for immediate access

// Removed skeleton loader for immediate display

// Modern Feature Card Component
const ModernFeatureCard = ({ icon, title, description, gradient }) => (
  <div className="group">
    <div className="relative overflow-hidden rounded-2xl">
      <div className="absolute inset-0 bg-white dark:bg-gray-800 backdrop-blur-xl rounded-2xl shadow-xl"></div>
      <div className="relative z-10 p-6">
        <div className="flex items-center gap-4 mb-3">
          <div className={`p-3 rounded-xl ${gradient} shadow-lg`}>
            <div className="text-white text-xl">{icon}</div>
          </div>
          <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100">
            {title}
          </h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

// Add password validation function from RegisterPage
const validatePassword = (password) => {
  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  const errors = [];
  if (password.length < minLength)
    errors.push(`Password must be at least ${minLength} characters long`);
  if (!hasUpperCase)
    errors.push('Password must contain at least one uppercase letter');
  if (!hasLowerCase)
    errors.push('Password must contain at least one lowercase letter');
  if (!hasNumbers) errors.push('Password must contain at least one number');
  if (!hasSpecialChar)
    errors.push('Password must contain at least one special character');

  return errors;
};

// Password Requirements Component
const PasswordRequirements = ({ password = '', isVisible = true, className = '' }) => {
  const requirements = [
    { test: password.length >= 8, text: 'At least 8 characters' },
    { test: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { test: /[a-z]/.test(password), text: 'One lowercase letter' },
    { test: /\d/.test(password), text: 'One number' },
    { test: /[!@#$%^&*(),.?":{}|<>]/.test(password), text: 'One special character' }
  ];

  if (!isVisible) return null;

  return (
    <div className={`mt-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 ${className}`}>
      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Password Requirements:</p>
      <ul className="space-y-1">
        {requirements.map((req, index) => (
          <li key={index} className="flex items-center gap-2 text-xs">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
              password.length > 0 
                ? req.test 
                  ? 'bg-green-500 text-white' 
                  : 'bg-red-500 text-white'
                : 'bg-gray-300 dark:bg-gray-600'
            }`}>
              {password.length > 0 && (
                req.test ? '✓' : '×'
              )}
            </div>
            <span className={`${
              password.length > 0 
                ? req.test 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
                : 'text-gray-600 dark:text-gray-400'
            }`}>
              {req.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Modern Loader Component
const ModernLoader = () => (
  <div className="flex items-center gap-2">
    <div className="w-4 h-4 bg-white rounded-full animate-bounce"></div>
    <div className="w-4 h-4 bg-white rounded-full animate-bounce delay-100"></div>
    <div className="w-4 h-4 bg-white rounded-full animate-bounce delay-200"></div>
  </div>
);

// Removed complex typing animation for immediate display

// Removed FeedbackCard mock report component

const LoginPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  // const { isDarkMode } = useTheme(); // Removed - no longer needed without animations

  /* ------------------------------------------------ state */
  const [mode, setMode] = useState('login'); // login | register | verify
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationInProgress, setRegistrationInProgress] = useState(false);

  // login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);

  // 2FA
  const [verificationCode, setVerificationCode] = useState('');

  // register
  const [referenceCode, setReferenceCode] = useState('');
  const [invite, setInvite] = useState(null); // { email, role, id }
  const [name, setName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');

  /* ------------------------------------------------ effects */
  useEffect(() => {
    if (currentUser && !registrationInProgress) navigate('/');
    return () => twoFactorAuth.clearRecaptcha();
  }, [currentUser, navigate, registrationInProgress]);

  /* ------------------------------------------------ actions */
  const handleNavigateToMode = (newMode) => {
    setMode(newMode);
  };
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (loginRateLimiter.getRemainingAttempts(email) <= 0) {
        toast.error('Too many attempts – wait 15 min');
        setLoading(false);
        return;
      }

      await setPersistence(
        auth,
        remember ? browserLocalPersistence : browserSessionPersistence
      );

      const creds = await signInWithEmailAndPassword(auth, email, password);
      const docRef = doc(db, 'users', creds.user.uid);
      const snap = await getDoc(docRef);
      if (!snap.exists()) throw new Error('not‑found');
      const data = snap.data();
      if (!data.active) throw new Error('deactivated');

      if (data.requireTwoFactor) {
        setMode('verify');
        await twoFactorAuth.sendVerificationCode(data.phoneNumber, 'verify-btn');
        setLoading(false);
        return;
      }

      loginRateLimiter.resetAttempts(email);
      await logUserActivity(creds.user.uid, data.name, 'LOGIN_SUCCESS');
      toast.success('Welcome back');
      await presenceTracker.startTracking();
          navigate('/');
    } catch (err) {
      console.error(err);
      loginRateLimiter.isRateLimited(email);
      toast.error('Login failed');
          setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode) return toast.error('Enter the code');
      setLoading(true);
    try {
      await twoFactorAuth.verifyCode(verificationCode);
      await updateDoc(doc(db, 'users', auth.currentUser.uid), { lastLogin: new Date().toISOString() });
      await presenceTracker.startTracking();
      navigate('/');
    } catch {
      toast.error('Invalid code');
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    if (!email) return toast.error('Enter your email first');
      setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Reset link sent');
    } catch {
      toast.error('Could not send email');
    } finally {
      setLoading(false);
    }
  };

  /* ----‑‑‑ register */
  const validateRef = async () => {
    if (!referenceCode) return toast.error('Enter a reference code');
      setLoading(true);
    try {
      const q = query(collection(db, 'invites'), where('referenceCode', '==', referenceCode), where('used', '==', false));
      const snap = await getDocs(q);
      if (snap.empty) throw new Error();
      const inviteDoc = snap.docs[0];
      setInvite({ id: inviteDoc.id, ...inviteDoc.data() });
      toast.success('Code valid');
    } catch {
      toast.error('Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const errs = validatePassword(regPassword);
    if (errs.length) return errs.forEach(toast.error);
    if (regPassword !== regConfirm) return toast.error('Passwords do not match');
    
    setRegistrationInProgress(true);
      setLoading(true);
    
    try {
      const userCred = await createUserWithEmailAndPassword(auth, invite.email, regPassword);
      await setDoc(doc(db, 'users', userCred.user.uid), {
        name,
        idNumber,
        email: invite.email,
        role: invite.role,
        active: true,
        archived: false,
        lastPasswordChange: new Date().toISOString(),
        requirePasswordChange: false,
      });
      await updateDoc(doc(db, 'invites', invite.id), { used: true });
      
      // Stop loading before showing toast
      setLoading(false);
      toast.success('Registration successful!');
      toast.info('Logging in...');
      
      // Let toasts display, then allow useEffect to navigate by resetting the flag
      setTimeout(() => {
        setRegistrationInProgress(false);
      }, 1000);
    } catch (err) {
      console.error(err);
      toast.error('Registration failed');
      setRegistrationInProgress(false);
        setLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */

  // Modern input styling with enhanced animations
  const modernInput = 'w-full px-4 py-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-2 border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-4 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 focus:border-blue-500 dark:focus:border-blue-400 transition-all duration-300 hover:bg-white dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md';

  // Modern button styling with proper theme colors
  const modernButton = 'font-semibold py-4 px-6 rounded-xl shadow-lg';
  const primaryButton = `${modernButton} bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white`;
  const secondaryButton = `${modernButton} bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600`;

  // Feature data
  const features = [
    {
      icon: <FiShield />,
      title: 'Daily Observation Reports (DORs)',
      description: 'Create and manage detailed daily assessments of trainee performance with standardized evaluation criteria.',
      gradient: 'bg-gradient-to-br from-blue-600 to-cyan-600',
      delay: 0
    },
    {
      icon: <FiTrendingUp />,
      title: 'Real-Time Progress Tracking',
      description: 'Monitor trainee development through comprehensive reporting with automated validation and constraints.',
      gradient: 'bg-gradient-to-br from-cyan-600 to-indigo-600',
      delay: 0
    },
    {
      icon: <FiAward />,
      title: 'Smart Notification System',
      description: 'Instant alerts for report submissions, assignments, and critical updates with role-based targeting.',
      gradient: 'bg-gradient-to-br from-indigo-600 to-blue-600',
      delay: 0
    },
    {
      icon: <FiStar />,
      title: 'Comprehensive User Management',
      description: 'Advanced admin tools for trainee-trainer assignments, user invitations, and secure access control.',
      gradient: 'bg-gradient-to-br from-blue-600 to-indigo-600',
      delay: 0
    }
  ];

  /* ------------------------------------------------------------------ */

  return (
    <div className="min-h-screen w-full bg-slate-100 dark:bg-gray-900 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 dark:bg-blue-400/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-500/10 dark:bg-indigo-400/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-cyan-500/5 dark:bg-cyan-400/5 rounded-full blur-2xl"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 text-center w-full pt-8 pb-4 px-4">
        <div className="max-w-5xl mx-auto">
          <div>
            <img 
              src={logo} 
              alt="Cheverly Police Department" 
              className="w-32 h-32 sm:w-36 sm:h-36 md:w-40 md:h-40 mx-auto mb-4 drop-shadow-2xl" 
              style={{
                filter: 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.3)) drop-shadow(0 0 40px rgba(59, 130, 246, 0.2))'
              }}
            />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 text-gray-900 dark:text-gray-100 font-login-special">
            Cheverly Police Department
          </h1>
          <h2 className="text-xl sm:text-2xl font-medium text-blue-600 dark:text-blue-400 font-login-special">
            Field Training Portal
          </h2>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex-1 w-full flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        {/* Form Pages - Two Column Layout */}
        <div className="w-full max-w-7xl mx-auto grid lg:grid-cols-12 gap-8 xl:gap-16 items-center">
          
          {/* Left side - 7 columns */}
          <div className="lg:col-span-7 relative">
            {/* Feature cards - desktop only */}
            <div className="hidden lg:grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              {features.map((feature, i) => (
                <ModernFeatureCard key={i} {...feature} />
              ))}
            </div>
            
            {/* Removed mock DOR report card */}
          </div>

          {/* Right side - 5 columns */}
          <div className="lg:col-span-5">
            
            
            {/* Removed mock DOR report card */}

            {/* LOGIN */}
            {mode === 'login' && (
              <div className="relative group">
                {/* Glassmorphism background */}
                <div className="absolute inset-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-700/50 shadow-2xl"></div>
                
                {/* Content */}
                <form onSubmit={handleLogin} className="relative z-10 p-8">
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 font-login-special mb-2">
                      Sign In
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Welcome back to your training portal
                    </p>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="relative group">
                      <label htmlFor="email" className="sr-only">Email</label>
                      <FiMail className="absolute left-4 top-4 text-gray-400 dark:text-gray-500 transition-colors duration-300 group-focus-within:text-blue-500 dark:group-focus-within:text-blue-400" />
                      <input 
                        id="email" 
                        type="email" 
                        required 
                        autoComplete="email" 
                        className={`${modernInput} pl-12`} 
                        placeholder="Enter your email address" 
                        value={email} 
                        onChange={(e) => setEmail(e.target.value)} 
                      />
                    </div>
                    
                    <div className="relative group">
                      <label htmlFor="password" className="sr-only">Password</label>
                      <FiLock className="absolute left-4 top-4 text-gray-400 dark:text-gray-500 transition-colors duration-300 group-focus-within:text-blue-500 dark:group-focus-within:text-blue-400" />
                      <input 
                        id="password" 
                        type={showPassword ? 'text' : 'password'} 
                        required 
                        autoComplete="current-password" 
                        className={`${modernInput} pl-12 pr-12`} 
                        placeholder="Enter your password" 
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)} 
                      />
                      <button 
                        type="button" 
                        aria-label={showPassword ? 'Hide password' : 'Show password'} 
                        onClick={() => setShowPassword((p) => !p)} 
                        className="absolute right-4 top-4 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
                      >
                        {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-6 mb-8">
                    <label className="flex items-center gap-3 cursor-pointer select-none group">
                      <div className="relative">
                        <input 
                          type="checkbox" 
                          checked={remember} 
                          onChange={(e) => setRemember(e.target.checked)} 
                          className="sr-only" 
                        />
                        <div className={`w-5 h-5 rounded border-2 transition-all duration-200 ${remember ? 'bg-blue-600 dark:bg-blue-500 border-blue-600 dark:border-blue-500' : 'border-gray-300 dark:border-gray-600'}`}>
                          {remember && <FiUser className="w-3 h-3 text-primary-light dark:text-primary-dark absolute top-0.5 left-0.5" />}
                        </div>
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-300">Remember me</span>
                    </label>
                    <button 
                      type="button" 
                      onClick={handleForgot} 
                      className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors duration-200"
                    >
                      Forgot password?
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <button 
                      type="submit" 
                      disabled={loading} 
                      className={`${primaryButton} w-full flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed group`}
                    >
                      {loading ? <ModernLoader /> : (
                        <>
                          <span className="font-semibold">Sign In</span>
                          <FiArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                    
                    <button 
                      type="button" 
                      onClick={() => handleNavigateToMode('register')} 
                      className={`${secondaryButton} w-full`}
                    >
                      Create new account
                    </button>
                    
                    <div className="flex gap-4 mt-6">
                      <button 
                        type="button" 
                        onClick={() => handleNavigateToMode('login')} 
                        className="flex-1 text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                      >
                        ← Back
                      </button>
                      <button 
                        type="button" 
                        onClick={() => handleNavigateToMode('login')} 
                        className="flex-1 text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                      >
                        🏠 Home
                      </button>
                    </div>
                  </div>
                </form>
                
              </div>
            )}

            {/* VERIFY */}
            {mode === 'verify' && (
              <div className="relative group">
                {/* Glassmorphism background */}
                <div className="absolute inset-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-700/50 shadow-2xl"></div>
                
                {/* Content */}
                <div className="relative z-10 p-8">
                  <h3 className="text-3xl font-bold mb-6 text-gray-900 dark:text-gray-100 text-center font-login-special">
                    Two‑Factor Verification
                  </h3>
                  <p className="mb-8 text-gray-600 dark:text-gray-300 text-center leading-relaxed">
                    Enter the verification code sent to your phone
                  </p>
                  
                  <div className="mb-8">
                    <label htmlFor="verification" className="sr-only">Verification Code</label>
                    <input 
                      id="verification" 
                      className={`${modernInput} text-center text-xl tracking-widest`} 
                      value={verificationCode} 
                      onChange={(e) => setVerificationCode(e.target.value)} 
                      placeholder="123456" 
                      inputMode="numeric" 
                      maxLength="6"
                    />
                  </div>
                  
                  <div className="space-y-4">
                    <button 
                      onClick={handleVerify} 
                      disabled={loading} 
                      id="verify-btn" 
                      className={`${primaryButton} w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {loading ? <ModernLoader /> : (
                        <>
                          <FiShield className="w-5 h-5" />
                          Verify Code
                        </>
                      )}
                    </button>
                    
                    <div className="flex gap-4 mt-6">
                      <button 
                        onClick={() => handleNavigateToMode('login')} 
                        className="flex-1 text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                      >
                        ← Back
                      </button>
                      <button 
                        onClick={() => handleNavigateToMode('login')} 
                        className="flex-1 text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                      >
                        🏠 Home
                      </button>
                    </div>
                  </div>
                </div>
                
              </div>
            )}

            {/* REGISTER */}
            {mode === 'register' && (
              <div className="relative group">
                {/* Glassmorphism background */}
                <div className="absolute inset-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-700/50 shadow-2xl"></div>
                
                {/* Content */}
                <div className="relative z-10 p-8 max-h-[80vh] overflow-y-auto">
                  {!invite ? (
                    <>
                      <h3 className="text-3xl font-bold mb-6 text-gray-900 dark:text-gray-100 text-center font-login-special">
                        Validate Reference Code
                      </h3>
                      
                      <div className="mb-8">
                        <div className="relative">
                          <label htmlFor="reference" className="sr-only">Reference Code</label>
                          <FiShield className="absolute left-4 top-4 text-gray-500 dark:text-gray-400" />
                          <input 
                            id="reference" 
                            className={`${modernInput} pl-12 text-center`} 
                            placeholder="Enter Reference Code" 
                            value={referenceCode} 
                            onChange={(e) => setReferenceCode(e.target.value)} 
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <button 
                          onClick={validateRef} 
                          disabled={loading} 
                          className={`${primaryButton} w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {loading ? <ModernLoader /> : (
                            <>
                              <FiShield className="w-5 h-5" />
                              Validate Code
                            </>
                          )}
                        </button>
                        
                        <div className="flex gap-4 mt-6">
                          <button 
                            onClick={() => handleNavigateToMode('login')} 
                            className="flex-1 text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          >
                            ← Back to Login
                          </button>
                          <button 
                            onClick={() => handleNavigateToMode('login')} 
                            className="flex-1 text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          >
                            🏠 Login
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <form onSubmit={handleRegister}>
                      <h3 className="text-3xl font-bold mb-6 text-gray-900 dark:text-gray-100 text-center font-login-special">
                        Create Account
                      </h3>
                      
                      <div className="space-y-6">
                        <div>
                          <input 
                            className={`${modernInput} opacity-60`} 
                            value={invite.email} 
                            disabled 
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Assigned email address</p>
                        </div>
                        
                        <div className="relative">
                          <label htmlFor="name" className="sr-only">Rank Lastname</label>
                          <FiUser className="absolute left-4 top-4 text-gray-500 dark:text-gray-400" />
                          <input 
                            id="name" 
                            className={`${modernInput} pl-12`} 
                            placeholder="Rank Lastname" 
                            value={name} 
                            onChange={(e) => setName(e.target.value)} 
                            required 
                          />
                        </div>
                        
                        <div className="relative">
                          <label htmlFor="idNumber" className="sr-only">ID Number</label>
                          <FiUser className="absolute left-4 top-4 text-gray-500 dark:text-gray-400" />
                          <input
                            id="idNumber"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            className={`${modernInput} pl-12`}
                            placeholder="ID Number"
                            value={idNumber}
                            onChange={(e) => {
                              const numeric = e.target.value.replace(/\D/g, '');
                              setIdNumber(numeric);
                            }}
                            required 
                          />
                        </div>
                        
                        <div>
                        <div className="relative">
                          <label htmlFor="regPassword" className="sr-only">Password</label>
                          <FiLock className="absolute left-4 top-4 text-gray-500 dark:text-gray-400" />
                          <input 
                            id="regPassword" 
                            type={showPassword ? 'text' : 'password'} 
                            className={`${modernInput} pl-12 pr-12`} 
                            placeholder="Password" 
                            value={regPassword} 
                            onChange={(e) => setRegPassword(e.target.value)} 
                            required 
                          />
                          <button 
                            type="button" 
                            aria-label={showPassword ? 'Hide password' : 'Show password'} 
                            onClick={() => setShowPassword((p) => !p)} 
                            className="absolute right-4 top-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none transition-colors duration-200"
                          >
                            {showPassword ? <FiEyeOff /> : <FiEye />}
                          </button>
                          </div>
                          <PasswordRequirements password={regPassword} />
                        </div>
                        
                        <div className="relative">
                          <label htmlFor="regConfirm" className="sr-only">Confirm Password</label>
                          <FiLock className="absolute left-4 top-4 text-gray-500 dark:text-gray-400" />
                          <input 
                            id="regConfirm" 
                            type={showPassword ? 'text' : 'password'} 
                            className={`${modernInput} pl-12`} 
                            placeholder="Confirm Password" 
                            value={regConfirm} 
                            onChange={(e) => setRegConfirm(e.target.value)} 
                            required 
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-4 mt-8">
                        <button 
                          type="submit" 
                          disabled={loading} 
                          className={`${primaryButton} w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {loading ? <ModernLoader /> : (
                            <>
                              <FiUser className="w-5 h-5" />
                              Create Account
                            </>
                          )}
                        </button>
                        
                        <div className="flex gap-4 mt-6">
                          <button 
                            type="button" 
                            onClick={() => {
                              setInvite(null);
                              setReferenceCode('');
                            }} 
                            className="flex-1 text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          >
                            ← Back
                          </button>
                          <button 
                            type="button" 
                            onClick={() => handleNavigateToMode('login')} 
                            className="flex-1 text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          >
                            🏠 Home
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
                
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
